<template>
  <div class="timeline-wrapper">
    <div class="timeline" @dblclick="addNewNode" :style="{ height: timelineHeight + 'px' }">
      <div class="timeline-line" :style="{ height: timelineHeight + 'px' }"></div>
      <div 
        v-for="(group, index) in groupedItems" 
        :key="index" 
        class="timeline-item"
        :class="group.position"
        :style="{ 
          top: getNodePosition(group.year) + 'px',
          height: getNodeHeight(group.year) + 'px'
        }"
        :data-year="group.year"
        @click.stop="editNode(group)"
        @dblclick.stop
      >
        <div class="timeline-dot"></div>
        <div 
          class="timeline-content" 
          :class="{ 'editable': editable }"
          :style="{ width: getNodeWidth(group.year) + 'px' }"
        >
          <div class="timeline-year">{{ group.year }}</div>
          <div v-for="(item, itemIndex) in group.items" :key="itemIndex" class="timeline-item-content">
            <!-- 只有当标题与前一个项目的标题不同，或者是第一个项目时才显示标题 -->
            <div v-if="shouldShowTitle(group.items, itemIndex)" class="timeline-title">
              {{ item.title }}
              <span 
                v-if="item.subtitle && item.subtitle.trim() !== ''" 
                class="collapse-toggle" 
                @click.stop="toggleItemCollapse(group.year, itemIndex)"
              >
                <i :class="isItemCollapsed(group.year, itemIndex) ? 'el-icon-arrow-right' : 'el-icon-arrow-down'"></i>
              </span>
            </div>
            <div 
              v-if="item.subtitle && !isItemCollapsed(group.year, itemIndex)" 
              class="timeline-subtitle"
            >
              {{ item.subtitle }}
            </div>
            <div v-if="group.items.length > 1 && itemIndex < group.items.length - 1" class="item-separator"></div>
          </div>
          <div v-if="editable" class="edit-icon">
            <i class="el-icon-edit-outline"></i>
          </div>
          <!-- 拖拽手柄 -->
          <div 
            class="resize-handle"
            :class="group.position"
            @mousedown="startResize($event, group.year)"
          ></div>
        </div>
      </div>
    </div>
    
    <!-- 节点编辑弹窗 -->
    <el-dialog
      :title="originalGroupIndex === -1 ? '新增时间节点' : '编辑时间节点'"
      :visible.sync="showEditDialog"
      width="500px"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :append-to-body="true"
    >
      <div v-if="editingNode">
        <el-form :model="editingNode" label-width="80px" size="small">
          <el-form-item label="年份">
            <el-input-number 
              v-model="editingNode.year" 
              :min="1900" 
              :max="2100" 
              style="width: 125px;"
              @change="updateYear"
            ></el-input-number>
          </el-form-item>
          <el-form-item label="位置">
            <el-select v-model="editingNode.position" placeholder="选择位置">
              <el-option label="左侧" value="left"></el-option>
              <el-option label="右侧" value="right"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        
        <div class="items-editor">
          <div class="items-header">
            <h4>内容项目</h4>
            <el-button size="mini" type="primary" @click="addContentItem">添加项目</el-button>
          </div>
          
          <div v-for="(item, index) in editingNode.items" :key="index" class="content-item">
            <el-form :model="item" size="small">
              <div class="item-controls">
                <span>项目 {{ index + 1 }}</span>
                <el-button 
                  v-if="editingNode.items.length > 1" 
                  size="mini" 
                  type="danger" 
                  @click="removeContentItem(index)"
                >
                  删除
                </el-button>
              </div>
              <el-form-item label="标题" label-width="60px">
                <el-input v-model="item.title" placeholder="请输入标题"></el-input>
              </el-form-item>
              <el-form-item label="子标题" label-width="60px">
                <el-input v-model="item.subtitle" placeholder="请输入子标题（可选）"></el-input>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button v-if="originalGroupIndex !== -1" type="danger" @click="deleteNode">删除节点</el-button>
        <el-button type="primary" @click="saveEdit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'TimelineComponent',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showEditDialog: false,
      editingNode: null,
      originalGroupIndex: -1,
      collapsedItems: new Set(), // 存储折叠状态的项目ID (year-itemIndex)
      nodeWidths: {}, // 存储每个节点的宽度 {year: width}
      nodeHeights: {}, // 存储每个节点的高度 {year: height}
      nodePositions: {}, // 存储每个节点的位置 {year: topPosition}
      isResizing: false,
      resizingYear: null,
      minNodeSpacing: 20 // 节点之间的最小间距
    }
  },
  computed: {
    groupedItems() {
      // 按年份分组
      const groups = {};
      this.items.forEach(item => {
        if (!groups[item.year]) {
          groups[item.year] = {
            year: item.year,
            position: item.position,
            items: []
          };
        }
        groups[item.year].items.push({
          title: item.title,
          subtitle: item.subtitle
        });
        // 如果有多个相同年份的节点，使用最后一个的位置
        groups[item.year].position = item.position;
      });
      
      // 转换为数组并排序
      return Object.values(groups).sort((a, b) => a.year - b.year);
    },
    
    // 计算时间轴总高度
    timelineHeight() {
      if (this.groupedItems.length === 0) return 500;
      
      // 计算所有节点的总高度
      let totalHeight = 50; // 顶部留白
      this.groupedItems.forEach((group, index) => {
        const nodeHeight = this.getNodeHeight(group.year);
        totalHeight += nodeHeight;
        if (index < this.groupedItems.length - 1) {
          totalHeight += this.minNodeSpacing; // 节点间距
        }
      });
      totalHeight += 100; // 底部留白
      
      return Math.max(totalHeight, 500);
    },
    
    // 计算节点位置
    calculatedNodePositions() {
      const positions = {};
      let currentTop = 50; // 起始位置
      
      this.groupedItems.forEach((group, index) => {
        positions[group.year] = currentTop;
        const nodeHeight = this.getNodeHeight(group.year);
        currentTop += nodeHeight + this.minNodeSpacing;
      });
      
      return positions;
    }
  },
  methods: {
    editNode(group) {
      if (!this.editable) return;
      
      console.log('编辑节点:', group);
      this.originalGroupIndex = this.groupedItems.findIndex(g => g.year === group.year);
      this.editingNode = JSON.parse(JSON.stringify(group));
      this.showEditDialog = true;
    },
    
    addContentItem() {
      this.editingNode.items.push({
        title: '',
        subtitle: ''
      });
    },
    
    removeContentItem(index) {
      this.editingNode.items.splice(index, 1);
    },
    
    updateYear() {
      // 年份改变时的处理逻辑
    },
    
    saveEdit() {
      // 验证年份
      if (!this.editingNode.year) {
        this.$message.error('请填写年份');
        return;
      }
      
      // 验证：至少要有一个项目有标题
      const hasValidTitle = this.editingNode.items.some(item => item.title && item.title.trim() !== '');
      
      if (!hasValidTitle) {
        this.$message.error('请至少填写一个项目的标题');
        return;
      }
      
      // 将编辑后的节点转换回原始格式，过滤掉空标题的项目
      const newItems = [];
      this.editingNode.items.forEach(item => {
        if (item.title && item.title.trim() !== '') {
          newItems.push({
            year: this.editingNode.year,
            title: item.title.trim(),
            subtitle: item.subtitle ? item.subtitle.trim() : '',
            position: this.editingNode.position
          });
        }
      });
      
      // 如果过滤后没有有效项目，不保存
      if (newItems.length === 0) {
        this.$message.error('请至少填写一个项目的标题');
        return;
      }
      
      let updatedItems;
      if (this.originalGroupIndex === -1) {
        // 新节点
        updatedItems = [...this.items, ...newItems];
      } else {
        // 编辑现有节点 - 找到原来这个年份的所有项目并替换
        const originalYear = this.groupedItems[this.originalGroupIndex].year;
        updatedItems = this.items.filter(item => item.year !== originalYear);
        updatedItems.push(...newItems);
      }
      
      this.$emit('update-items', updatedItems);
      this.showEditDialog = false;
    },
    
    deleteNode() {
      const yearToDelete = this.editingNode.year;
      const updatedItems = this.items.filter(item => item.year !== yearToDelete);
      this.$emit('update-items', updatedItems);
      this.showEditDialog = false;
    },
    
    addNewNode(event) {
      if (!this.editable) return;
      
      console.log('添加新节点');
      
      // 创建新节点，默认信息为空
      this.editingNode = {
        year: null,
        position: 'right',
        items: [{
          title: '',
          subtitle: ''
        }]
      };
      
      this.originalGroupIndex = -1; // 标记为新节点
      this.showEditDialog = true;
    },
    
    // 生成项目唯一ID
    getItemId(year, itemIndex) {
      return `${year}-${itemIndex}`;
    },
    
    // 检查单个项目是否折叠
    isItemCollapsed(year, itemIndex) {
      const itemId = this.getItemId(year, itemIndex);
      return this.collapsedItems.has(itemId);
    },
    
    // 切换单个项目的折叠状态
    toggleItemCollapse(year, itemIndex) {
      const itemId = this.getItemId(year, itemIndex);
      if (this.collapsedItems.has(itemId)) {
        this.collapsedItems.delete(itemId);
      } else {
        this.collapsedItems.add(itemId);
      }
      
      // 重新计算该节点的高度
      this.$delete(this.nodeHeights, year);
      
      // 强制更新视图
      this.$forceUpdate();
    },
    
    // 判断是否应该显示标题
    // 如果标题为空或为"未命名标题"，不显示；如果是第一个项目，或者当前项目的标题与前一个项目的标题不同，则显示标题
    shouldShowTitle(items, itemIndex) {
      // 获取当前项目的标题
      const currentTitle = items[itemIndex].title ? items[itemIndex].title.trim() : '';
      
      // 如果当前标题为空或者是"未命名标题"等占位文本，不显示标题
      if (!currentTitle || currentTitle === '未命名标题') {
        return false;
      }
      
      // 第一个项目如果标题不为空且不是占位文本，显示标题
      if (itemIndex === 0) {
        return true;
      }
      
      // 获取前一个项目的标题
      const previousTitle = items[itemIndex - 1].title ? items[itemIndex - 1].title.trim() : '';
      
      // 如果前一个标题是占位文本，当前标题不为空且不是占位文本，显示标题
      if (previousTitle === '未命名标题' || !previousTitle) {
        return true;
      }
      
      // 如果标题不同，显示标题；如果标题相同，不显示标题
      return currentTitle !== previousTitle;
    },
    
    // 获取节点宽度
    getNodeWidth(year) {
      return this.nodeWidths[year] || 300; // 默认宽度300px
    },
    
    // 获取节点高度
    getNodeHeight(year) {
      // 如果已经缓存了高度，直接返回
      if (this.nodeHeights[year]) {
        return this.nodeHeights[year];
      }
      
      // 根据内容估算高度
      const group = this.groupedItems.find(g => g.year === year);
      if (!group) return 80; // 默认高度
      
      let estimatedHeight = 40; // 年份行高度
      
      group.items.forEach((item, itemIndex) => {
        // 标题行高度
        estimatedHeight += 20;
        
        // 如果有子标题且未折叠，增加子标题高度
        if (item.subtitle && !this.isItemCollapsed(year, itemIndex)) {
          // 根据子标题长度估算行数
          const subtitleLines = Math.ceil(item.subtitle.length / 40); // 假设每行40个字符
          estimatedHeight += subtitleLines * 16; // 每行16px高度
        }
        
        // 项目间分隔符高度
        if (group.items.length > 1) {
          estimatedHeight += 10;
        }
      });
      
      // 添加内边距
      estimatedHeight += 28; // 上下内边距
      
      // 最小高度限制
      const finalHeight = Math.max(estimatedHeight, 80);
      
      // 缓存计算结果
      this.$set(this.nodeHeights, year, finalHeight);
      
      return finalHeight;
    },
    
    // 获取节点位置
    getNodePosition(year) {
      return this.calculatedNodePositions[year] || 0;
    },
    
    // 重新计算所有节点高度
    recalculateNodeHeights() {
      this.nodeHeights = {};
      this.$nextTick(() => {
        this.measureActualNodeHeights();
      });
    },
    
    // 测量实际节点高度（DOM渲染后）
    measureActualNodeHeights() {
      this.groupedItems.forEach(group => {
        const nodeElement = this.$el.querySelector(`[data-year="${group.year}"] .timeline-content`);
        if (nodeElement) {
          const actualHeight = nodeElement.offsetHeight;
          this.$set(this.nodeHeights, group.year, actualHeight);
        }
      });
    },
    
    // 开始拖拽调整宽度
    startResize(event, year) {
      event.preventDefault();
      event.stopPropagation();
      
      this.isResizing = true;
      this.resizingYear = year;
      
      const startX = event.clientX;
      const startWidth = this.getNodeWidth(year);
      
      // 找到对应的节点位置
      const group = this.groupedItems.find(g => g.year === year);
      const isLeftNode = group && group.position === 'left';
      
      const handleMouseMove = (e) => {
        if (!this.isResizing) return;
        
        const deltaX = e.clientX - startX;
        // 左侧节点：向左拖拽增加宽度，向右拖拽减少宽度
        // 右侧节点：向右拖拽增加宽度，向左拖拽减少宽度
        const widthDelta = isLeftNode ? -deltaX : deltaX;
        const newWidth = Math.max(200, Math.min(600, startWidth + widthDelta)); // 限制宽度范围200-600px
        
        this.$set(this.nodeWidths, year, newWidth);
      };
      
      const handleMouseUp = () => {
        this.isResizing = false;
        this.resizingYear = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }
  },
  
  watch: {
    // 监听items变化，重新计算高度
    items: {
      handler() {
        this.recalculateNodeHeights();
      },
      deep: true
    }
  },
  
  mounted() {
    // 组件挂载后测量实际高度
    this.$nextTick(() => {
      this.measureActualNodeHeights();
    });
  },
  
  updated() {
    // 组件更新后重新测量高度
    this.$nextTick(() => {
      this.measureActualNodeHeights();
    });
  }
}
</script>

<style lang="scss" scoped>
.timeline-wrapper {
  width: 100%;
  height: 100%;
  padding: 24px;
  background: linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%);
  position: relative;
  overflow-y: auto;
}

.timeline {
  position: relative;
  max-width: 100%;
  margin: 0 auto;
  min-height: calc(100vh - 150px);
  
  // 动态计算高度以适应所有节点
  &::after {
    content: '';
    display: block;
    height: 100px; // 底部留白
  }
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color:rgb(245,248,250);
  border-left: 2px dashed rgba(99, 102, 241, 0.3);
  border-right: 1px solid rgba(255, 255, 255, 0.6);
  transform: translateX(-50%);
  z-index: 1;
  border-radius: 2px;
  
}

.timeline-item {
  position: absolute;
  width: 100%;
  min-height: 80px;
  display: flex;
  align-items: flex-start;
  padding-top: 10px;
  
  &.left {
    justify-content: center;
    
    .timeline-content {
      position: absolute;
      right: 50%;
      margin-right: 30px;
      text-align: left;
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
      border: 1px solid #e2e8f0;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6);
        border-radius: 6px 6px 0 0;
      }
      
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: -8px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid #f1f5f9;
        border-top: 7px solid transparent;
        border-bottom: 7px solid transparent;
        z-index: 2;
      }
      
      .arrow-border {
        position: absolute;
        top: 50%;
        right: -9px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 9px solid #e2e8f0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        z-index: 1;
      }
    }
    
    .timeline-dot {
      position: absolute;
      left: calc(50% - 9px);
      top: 10px;
    }
  }
  
  &.right {
    justify-content: center;
    
    .timeline-content {
      position: absolute;
      left: 50%;
      margin-left: 30px;
      text-align: left;
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
      border: 1px solid #e2e8f0;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6);
        border-radius: 6px 6px 0 0;
      }
      
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: -8px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-right: 8px solid #f1f5f9;
        border-top: 7px solid transparent;
        border-bottom: 7px solid transparent;
        z-index: 2;
      }
      
      .arrow-border {
        position: absolute;
        top: 50%;
        left: -9px;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-right: 9px solid #e2e8f0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        z-index: 1;
      }
    }
    
    .timeline-dot {
      position: absolute;
      right: calc(50% - 7px);
      top: 10px;
    }
  }
}

.timeline-dot {
  position: absolute;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #6366f1;
  border-radius: 50%;
  z-index: 3;
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.25s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 4px;
    background: #6366f1;
    border-radius: 50%;
  }
  
  &:hover {
    border-color: #4f46e5;
    transform: scale(1.15);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3), 0 2px 6px rgba(0, 0, 0, 0.15);
    
    &::before {
      background: #4f46e5;
    }
  }
}

.timeline-content {
  position: relative;
  padding: 14px 16px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08);
  color: #374151;
  transition: all 0.25s ease;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  backdrop-filter: blur(10px);
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    
    &::before {
      opacity: 0.9;
    }
  }
  
  &.editable {
    cursor: pointer;
    
    &:hover .edit-icon {
      opacity: 1;
    }
  }
}

.edit-icon {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 16px;
  height: 16px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.15s ease;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  i {
    font-size: 10px;
    color: #6b7280;
  }
}

.timeline-item-content {
  .item-separator {
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(99, 102, 241, 0.2), transparent);
    margin: 8px 0;
    border-radius: 1px;
    
    &:last-child {
      display: none;
    }
  }
}

// 编辑弹窗样式
.items-editor {
  margin-top: 20px;
  
  .items-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    h4 {
      margin: 0;
      color: #374151;
      font-weight: 600;
    }
  }
  
  .content-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    background: linear-gradient(135deg, #fafafa 0%, #f3f4f6 100%);
    
    .item-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      span {
        font-weight: 600;
        color: #6b7280;
        font-size: 13px;
      }
    }
  }
}

.timeline-year {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #4f46e5;
  letter-spacing: -0.3px;
  word-wrap: break-word;
  word-break: break-word;
  text-shadow: 0 1px 2px rgba(79, 70, 229, 0.1);
}

.timeline-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
  color: #1f2937;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.timeline-subtitle {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  font-weight: 400;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  opacity: 0.8;
  transition: all 0.3s ease;
}

.collapse-toggle {
  cursor: pointer;
  color: #6b7280;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 3px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin-left: 6px;
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.1);
    color: #4f46e5;
  }
  
  i {
    font-size: 9px;
    transition: transform 0.2s ease;
  }
}

// 拖拽手柄样式
.resize-handle {
  position: absolute;
  top: 0;
  width: 6px;
  height: 100%;
  cursor: ew-resize;
  background: transparent;
  z-index: 10;
  
  &.left {
    left: -3px; // 左侧节点的手柄在左边
  }
  
  &.right {
    right: -3px; // 右侧节点的手柄在右边
  }
  
  &:hover {
    background: rgba(99, 102, 241, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 20px;
    background: rgba(99, 102, 241, 0.5);
    border-radius: 1px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .timeline-item {
    &.left, &.right {
      justify-content: flex-start;
      
      .timeline-content {
        width: 140px;
        margin: 0 0 0 50px;
        text-align: left;
        
        &::after, &::before {
          display: none;
        }
      }
    }
  }
  
  .timeline-line {
    left: 20px;
    border-left-color: #d1d5db;
  }
  
  .timeline-dot {
    left: 15px !important;
    right: auto !important;
  }
}

@media (max-width: 480px) {
  .timeline-item {
    .timeline-content {
      width: 130px;
      margin: 0 0 0 35px !important;
      padding: 10px 14px !important;
    }
  }
  
  .timeline-year {
    font-size: 16px !important;
  }
  
  .timeline-title {
    font-size: 13px !important;
  }
  
  .timeline-subtitle {
    font-size: 11px !important;
  }
  
  .timeline-dot {
    left: 12px !important;
  }
  
  .timeline-line {
    left: 17px !important;
  }
}

// 新增节点按钮样式
.add-node-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
}

.add-node-btn {
  border-radius: 6px;
  font-size: 13px;
  padding: 8px 16px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}
</style>
